import {
  KeyboardEvent,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { isMobile } from 'react-device-detect';

import { ArrowUpSvg } from '@cashier/cashier-assets';
import { InputApi } from '@cashier/cashier-model';
import { eventBus, EventFactory } from '@cashier/cashier-shared';

import { cn, SVGIcons } from '../../utils';
import { useSelect } from './useSelect';

import styles from './styles.module.scss';

interface SelectItem {
  value: string;
  label: string;
}

export interface SelectProps {
  ref?: Ref<InputApi>;
  name: string;
  options: SelectItem[];
  defaultValue?: string;
  onChange: (value: string) => void;
  version: 'v2' | 'v3';
  isDisabled: boolean;
  theme?: 'Default' | 'Platform';
  label?: string;
  isError?: boolean;
  isWebView?: boolean;
  direction?: 'rtl' | 'ltr';
  onBlur?: () => void;
}

export const Select = ({
  ref,
  name,
  options,
  defaultValue,
  onChange,
  label,
  theme = 'Default',
  isDisabled,
  isError = false,
  isWebView = false,
  version,
  direction,
}: SelectProps): ReactElement => {
  const [active, setActive] = useState(false);

  const { selectedItem, onItemSelected } = useSelect({
    ref,
    options,
    defaultValue,
    onChange,
  });

  const toggleSelect = () => {
    setActive(!active);
    onFocusSelectHandler();
  };

  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const extraHeightBuffer = 28;
    if (!active) {
      eventBus.emit(
        EventFactory.changeCashierHeight(
          (document.getElementById('root')?.clientHeight || 0) +
            extraHeightBuffer,
        ),
      );
    }
  }, [active]);

  const onItemClicked = useCallback(
    (key: SelectItem) => {
      onItemSelected(key);
      setActive(false);
    },
    [onItemSelected],
  );

  const onNativeSelectHandler = (item: {
    label: string;
    value: string;
  }): void => {
    onItemSelected(item);
    setActive(false);
  };

  const onFocusSelectHandler = (): void => {
    eventBus.emit(EventFactory.onFieldFocused(name));
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const { current } = optionsRef;
      const HEIGHT_ELEMENT_BUFFER = 10;

      if (
        current &&
        current.getBoundingClientRect().bottom + HEIGHT_ELEMENT_BUFFER >
          window.innerHeight
      ) {
        eventBus.emit(
          EventFactory.changeCashierHeight(
            current.getBoundingClientRect().bottom,
          ),
        );
      }

      if (
        optionsRef.current &&
        !optionsRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setActive(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keypress', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keypress', handleClickOutside);
    };
  }, [onItemClicked]);

  const keyboardHandler = (event: KeyboardEvent, key: SelectItem) => {
    if (event.key === 'Enter') {
      onItemSelected(key);
      setActive(false);
    }
    if (event.key === 'Escape') {
      setActive(false);
    }
  };

  const standardSelectorStyles =
    'v2' === version
      ? cn({
          [styles.standardSelect]: true,
          [styles.button]: true,
          [styles.platform]: theme === 'Platform',
          [styles.placeholder]: selectedItem?.value === '',
          [styles.error]: isError,
        })
      : cn({
          [styles.standardSelect]: true,
          [styles.parentContainer]: true,
          [styles.parentActive]: active && Boolean(options.length),
          [styles.parentEmptyField]: !selectedItem?.value,
          [styles.mobile]: isMobile,
          [styles.desktop]: !isMobile,
          [styles.selected]: selectedItem?.value !== '',
          [styles.error]: isError,
        });

  const customSelectorBodyStyles =
    'v2' === version
      ? cn({
          [styles.button]: true,
          [styles.default]: theme === 'Default',
          [styles.platform]: theme === 'Platform',
          [styles.active]: active && Boolean(options.length),
          [styles.error]: isError,
        })
      : cn({
          [styles.parentContainer]: true,
          [styles.parentActive]: active && Boolean(options.length),
          [styles.desktop]: true,
          [styles.error]: isError,
        });

  const placeholderStyles =
    'v2' === version
      ? cn({
          [styles.input]: true,
          [styles.default]: theme === 'Default',
          [styles.platform]: theme === 'Platform',
          [styles.active]:
            selectedItem?.label !== label && Boolean(selectedItem?.value),
          [styles.empty]: !selectedItem?.value,
          [styles.rtl]: direction === 'rtl',
        })
      : cn({
          [styles.parentPlaceholder]: true,
          [styles.parentEmptyField]: !selectedItem?.value,
        });

  const labelStyles =
    'v2' === version
      ? cn({
          [styles.label]: true,
          [styles.hiddenLabel]: true,
        })
      : cn([styles.parentLabel]);

  const desktopLabelStyles =
    'v2' === version
      ? cn({
          [styles.label]: true,
          [styles.hiddenLabel]: Boolean(selectedItem?.value),
        })
      : cn({
          [styles.parentDesktopLabel]: true,
          [styles.parentDesktopActiveLabel]: Boolean(
            active || selectedItem?.value,
          ),
        });

  if ('v3' === version || isMobile || isWebView) {
    return (
      <div
        className={cn({
          [styles.container]: true,
          [styles.standardSelectContainer]: true,
          [styles.desktop]: !isMobile,
        })}
      >
        {selectedItem?.value !== '' && (
          <label htmlFor={label} className={labelStyles}>
            {label}
          </label>
        )}
        <select
          data-test="select-input"
          id={name}
          name={name}
          disabled={isDisabled}
          onChange={({ target: { value } }) =>
            onNativeSelectHandler({ label: value, value })
          }
          value={selectedItem?.value || ''}
          className={standardSelectorStyles}
          onFocus={onFocusSelectHandler}
        >
          <option value="" disabled>
            {label}
          </option>
          {options.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <div className={styles.arrow}>
          <ArrowUpSvg />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {label && <div className={desktopLabelStyles}>{label}</div>}
      <div
        className={cn({
          [styles.formSelect]: true,
        })}
      >
        <button
          type="button"
          disabled={isDisabled}
          className={customSelectorBodyStyles}
          onClick={toggleSelect}
          ref={buttonRef}
          data-test="select-account-placeholder"
        >
          <span className={placeholderStyles}>{selectedItem?.label || ''}</span>
          <span
            className={cn({
              [styles.buttonArrow]: true,
              [styles.default]: theme === 'Default',
              [styles.platform]: theme === 'Platform',
              [styles.active]: active,
              [styles.rtl]: direction === 'rtl',
            })}
          >
            <svg
              className={version === 'v2' ? styles.svgIcon : styles.arrowSvg}
            >
              {version === 'v2' && <use href={`#${SVGIcons.baseArrow}`} />}
            </svg>
          </span>
        </button>
        {active && options.length !== 0 && (
          <div
            className={
              version === 'v2'
                ? styles.optionsContainer
                : styles.parentOptionsContainer
            }
          >
            <div
              className={cn({
                [styles.options]: version === 'v2',
                [styles.default]: theme === 'Default',
                [styles.platform]: theme === 'Platform',
                [styles.rtl]: direction === 'rtl',
              })}
              ref={optionsRef}
            >
              <div className={styles.optionsWrap}>
                {options.map((option) => {
                  const { label, value } = option;

                  if (!label || !value) return null;

                  return (
                    <div
                      data-test={`dropdown-select-${value}`}
                      tabIndex={0}
                      role="button"
                      className={cn({
                        [styles.option]: true,
                        [styles.selected]: selectedItem?.value === value,
                        [styles.platform]: theme === 'Platform',
                      })}
                      key={value}
                      onClick={() => onItemClicked(option)}
                      onKeyPress={(event) => keyboardHandler(event, option)}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
